import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authFileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import {
  Building2, MapPin, Wrench, FileSignature,
  Upload, X, Send, FileText, AlertCircle,
} from "lucide-react";
import { SupportChat } from "@/components/SupportChat";
import LocationPicker from "@/components/LocationPicker";

const SERVICE_CATEGORIES = [
  "Engine", "Transmission", "Brakes", "Electrical", "Suspension",
  "Body", "Tires", "HVAC", "Exhaust", "Diagnostics", "EV",
];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const WARRANTY_OPTIONS = ["Standard 12 mo / 12k mi", "Custom", "None"];

const blank = {
  legal_business_name: "",
  owner_full_name: "", owner_phone: "",
  is_mobile: false,
  address: { street: "", city: "", state: "", zip: "" },
  service_radius_miles: "",
  lat: null,
  lng: null,
  shop_photos: [],
  mobile_shop_photos: [],
  service_categories: [],
  years_experience: "",
  warranty_offered: "",
  agreements: { tos: false, privacy: false, anonymity: false, pricing_transparency: false, dispute_resolution: false },
};

const DRAFT_KEY = "shiftquote_shop_application_draft";

export default function ShopApplication({ embedded = false, onSubmitted } = {}) {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftSavedVisible, setDraftSavedVisible] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showMapConfirm, setShowMapConfirm] = useState(false);
  const [pendingCoords, setPendingCoords] = useState({ lat: null, lng: null });
  const debounceRef = useRef(null);
  const draftFadeRef = useRef(null);
  const serverLoaded = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shops/application");
        if (res.data) setForm({ ...blank, ...res.data, address: { ...blank.address, ...(res.data.address || {}) }, agreements: { ...blank.agreements, ...(res.data.agreements || {}) } });
      } catch {}
      // Overlay localStorage draft on top of server data (draft wins)
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setForm((f) => ({ ...f, ...parsed, address: { ...f.address, ...(parsed.address || {}) }, agreements: { ...f.agreements, ...(parsed.agreements || {}) } }));
          setDraftRestored(true);
          setTimeout(() => setDraftRestored(false), 5000);
        } catch {}
      }
      serverLoaded.current = true;
    })();
  }, []);

  // Auto-save to localStorage on every form change (debounced 800 ms)
  useEffect(() => {
    if (!serverLoaded.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      setDraftSavedVisible(true);
      if (draftFadeRef.current) clearTimeout(draftFadeRef.current);
      draftFadeRef.current = setTimeout(() => setDraftSavedVisible(false), 2000);
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  const setAgree = (k, v) => setForm((f) => ({ ...f, agreements: { ...f.agreements, [k]: v } }));
  const toggleCategory = (c) => set("service_categories", form.service_categories.includes(c) ? form.service_categories.filter((x) => x !== c) : [...form.service_categories, c]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setForm(blank);
    toast.success("Draft cleared");
  };

  const submit = async () => {
    setSubmitting(true);
    if (!form.lat || !form.lng) {
      toast.error("Please pin your shop location before submitting.");
      setSubmitting(false);
      // Scroll to the location section
      document.getElementById("location-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      const payload = {
        ...form,
        years_experience: parseInt(form.years_experience) || 0,
        service_radius_miles: parseInt(form.service_radius_miles) || 0,
        lat: form.lat,
        lng: form.lng,
      };
      await api.post("/shops/application", payload);
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Submitted — we’ll verify your business shortly");
      await checkAuth();
      if (embedded && onSubmitted) {
        onSubmitted();
      } else {
        navigate("/shop");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors[0]);
        console.warn("Application errors:", detail.errors);
      } else {
        toast.error(detail || "Failed to submit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  const formBody = (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-8">
          {draftRestored && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 mb-4 border border-[#34C759]/30 bg-[#34C759]/5 text-sm text-[#34C759]">
            <span>Draft restored — pick up where you left off.</span>
            <button type="button" onClick={() => setDraftRestored(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        )}
        <Section icon={Building2} title="1. Business Info" hint="We’ll use this to verify your business.">
            <Grid cols={2}>
              <Field label="BUSINESS NAME (*)">
                <input autoComplete="off" className="input-base" value={form.legal_business_name} onChange={(e) => set("legal_business_name", e.target.value)} data-testid="app-legal-name" />
              </Field>
              <Field label="OWNER FULL NAME (*)">
                <input autoComplete="off" className="input-base" value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} data-testid="app-owner-name" />
              </Field>
              <Field label="OWNER PHONE (*)">
                <input autoComplete="off" className="input-base" type="tel" value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} placeholder="(415) 555-0142" data-testid="app-owner-phone" />
              </Field>
            </Grid>
          </Section>

          <Section icon={MapPin} title="2. Location & service area" hint="If you’re a mobile shop, toggle below and upload photos of your rig instead of a storefront.">
            <div className="mb-5">
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_mobile} onChange={(e) => set("is_mobile", e.target.checked)} data-testid="app-is-mobile" className="w-4 h-4 accent-[#FF3B30]" />
                <span>I run a mobile shop (no fixed storefront)</span>
              </label>
            </div>
            {!form.is_mobile && (
              <Grid cols={2}>
                <Field label="STREET ADDRESS (*)" full>
                  <input className="input-base" value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} data-testid="app-addr-street" />
                </Field>
                <Field label="CITY (*)">
                  <input className="input-base" value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="app-addr-city" />
                </Field>
                <Field label="STATE (*)">
                  <select className="input-base" value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="app-addr-state">
                    <option value="">—</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="ZIP (*)">
                  <input className="input-base" value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} data-testid="app-addr-zip" />
                </Field>
              </Grid>
            )}
            {form.is_mobile && (
              <Grid cols={2} className="mt-5">
                <Field label="SERVICE RADIUS (MILES) (*)" hint="How far will you travel for a lead?">
                  <input className="input-base" type="number" min="1" max="500" value={form.service_radius_miles} onChange={(e) => set("service_radius_miles", e.target.value)} data-testid="app-radius" />
                </Field>
              </Grid>
            )}
            <div className="mt-6">
              <FileMultiUploader
                label={`${form.is_mobile ? "MOBILE RIG" : "SHOP"} PHOTOS * (MIN 3)`}
                paths={form.shop_photos}
                onChange={(p) => set("shop_photos", p)}
                accept="image/*"
                testid="app-photos"
              />
            </div>
          </Section>

          {/* --- CONFIRM YOUR SHOP LOCATION --- */}
          <section id="location-section" className="border border-white/10 bg-[#121214]">
            <div className="px-6 py-5 border-b border-white/10 flex items-start gap-3">
              <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold tracking-tight"><span>Pin Your Shop Location <span className="text-[#FF3B30]">*</span></span></h2>
                <p className="text-xs text-zinc-500 mt-0.5">Pin your exact location so nearby car owners can find you.</p>
                {(!form.lat || !form.lng) && (
                  <p className="text-xs text-[#FF3B30] mt-1">Required — shops without a pinned location won’t receive leads.</p>
                )}
                {form.lat && form.lng && (
                  <p className="text-xs text-green-500 mt-1">✓ Location pinned</p>
                )}
              </div>
            </div>
            <div className="px-6 py-6 space-y-4">
              {/* GPS accuracy banner — Fix 3 */}
              <div className="flex items-start gap-2.5 bg-[#1C1C1E] border border-[#FF9500]/30 rounded-lg px-4 py-3">
                <span className="text-[#FF9500] text-lg leading-none mt-0.5">📡</span>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  <strong className="text-white">Pro tip:</strong> For the most accurate pin, tap <strong>"Use My Current GPS Location"</strong> while you’re physically at your shop. If you verify by address, you’ll be able to adjust the pin on the map.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={geoLoading}
                  onClick={() => {
                    setGeoLoading(true);
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setPendingCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                        setGeoLoading(false);
                        setShowMapConfirm(true);
                      },
                      (err) => {
                        setGeoLoading(false);
                        toast.error("GPS failed: " + (err.message || "Permission denied"));
                      },
                      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
                    );
                  }}
                  className="bg-[#1C1C1E] border border-white/10 text-white text-sm px-4 py-2 rounded flex items-center gap-2 hover:border-white/20 transition-colors disabled:opacity-50"
                  data-testid="app-gps-btn"
                >
                  {geoLoading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    "📍"
                  )}
                  Use My Current GPS Location
                </button>
                {!form.is_mobile && (
                  <button
                    type="button"
                    disabled={geoLoading}
                    onClick={async () => {
                      const fullAddr = [form.address.street, form.address.city, form.address.state, form.address.zip].filter(Boolean).join(", ");
                      if (!fullAddr.trim()) { toast.error("Fill in your address first"); return; }
                      setGeoLoading(true);
                      try {
                        const res = await fetch(
                          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddr)}&limit=1`,
                          { headers: { "User-Agent": "ShiftQuote/1.0" } }
                        );
                        const data = await res.json();
                        if (data && data.length > 0) {
                          setPendingCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                          setShowMapConfirm(true);
                        } else {
                          toast.error("We couldn’t locate this address. Double-check your street address, or use the GPS option when you’re physically at your shop for the most accurate pin.");
                        }
                      } catch {
                        toast.error("Geocoding failed — check your connection");
                      } finally {
                        setGeoLoading(false);
                      }
                    }}
                    className="bg-[#1C1C1E] border border-white/10 text-white text-sm px-4 py-2 rounded flex items-center gap-2 hover:border-white/20 transition-colors disabled:opacity-50"
                    data-testid="app-geocode-btn"
                  >
                    {geoLoading ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      "📕"
                    )}
                    Verify Address on Map
                  </button>
                )}
              </div>
              {form.lat !== null && form.lng !== null ? (
                <p className="text-sm text-[#34C759]" data-testid="app-location-confirmed">
                  📍 Location pinned: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                </p>
              ) : (
                <p className="text-sm text-[#FF3B30]" data-testid="app-location-warning">
                  ⚠️ Location not confirmed — your shop may not appear in search results.
                </p>
              )}
            </div>
          </section>

          <Section icon={Wrench} title="3. Capabilities" hint="Helps us match the right leads to you.">
            <Field label="SERVICE CATEGORIES * (PICK AT LEAST ONE)" full>
              <div className="flex flex-wrap gap-2" data-testid="app-categories">
                {SERVICE_CATEGORIES.map((c) => {
                  const active = form.service_categories.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCategory(c)}
                      className={`text-xs uppercase tracking-widest px-3 py-2 transition-colors ${active ? "bg-[#FF3B30] text-white" : "border border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}
                      data-testid={`app-cat-${c.toLowerCase()}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Grid cols={2} className="mt-5">
              <Field label="YEARS OF BUSINESS EXPERIENCE (*)">
                <input className="input-base" type="number" min="0" max="100" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="app-years-exp" />
              </Field>
              <Field label="WARRANTY OFFERED (*)">
                <select className="input-base" value={form.warranty_offered} onChange={(e) => set("warranty_offered", e.target.value)} data-testid="app-warranty">
                  <option value="">Select…</option>
                  {WARRANTY_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </Field>
            </Grid>
          </Section>

          <Section icon={FileSignature} title="4. Agreements" hint="All five are required. These protect car owners and the platform.">
            <div className="space-y-3">
              <Agreement k="tos" checked={form.agreements.tos} onChange={setAgree} label="I agree to the ShiftQuote Terms of Service." />
              <Agreement k="privacy" checked={form.agreements.privacy} onChange={setAgree} label="I acknowledge the Privacy Policy." />
              <Agreement k="anonymity" checked={form.agreements.anonymity} onChange={setAgree} label="Anonymity policy — I will not attempt to identify, photograph, or contact car owners outside the app." important />
              <Agreement k="pricing_transparency" checked={form.agreements.pricing_transparency} onChange={setAgree} label="Pricing transparency — My quote is a preliminary estimate based on the described symptoms. I will notify the customer before proceeding if the actual scope differs after inspection." />
              <Agreement k="dispute_resolution" checked={form.agreements.dispute_resolution} onChange={setAgree} label="I agree to abide by ShiftQuote’s dispute-resolution process if a customer raises a complaint." />
            </div>
          </Section>

          <div className="border border-white/10 bg-[#121214] p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-zinc-400">
              <AlertCircle className="w-4 h-4 text-[#FF9500]" />
              You can edit and resubmit before an admin reviews your application.
            </div>
            {draftSavedVisible && (
              <span className="text-xs text-zinc-500 animate-pulse">Draft saved</span>
            )}
            <button type="button" onClick={clearDraft} className="text-xs text-zinc-500 hover:text-[#FF3B30] underline underline-offset-2">
              Clear draft
            </button>
            <button onClick={submit} disabled={submitting || !form.lat || !form.lng} className="btn-primary flex items-center gap-2" data-testid="app-submit-btn">
              <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit for verification"}
            </button>
          </div>
        </div>

      {/* Map Confirmation Modal — Fix 2 */}
      {showMapConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="map-confirm-overlay">
          <div className="bg-[#121214] border border-white/10 rounded-xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10">
              <h3 className="font-heading text-lg font-bold text-white">Confirm Your Shop Location</h3>
              <p className="text-xs text-zinc-400 mt-1">Drag the pin to fine-tune your exact position, then tap Confirm.</p>
            </div>
            <div className="h-[320px] w-full">
              <LocationPicker
                lat={pendingCoords.lat}
                lng={pendingCoords.lng}
                onChange={({ lat, lng }) => setPendingCoords({ lat, lng })}
              />
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowMapConfirm(false)}
                className="text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, lat: pendingCoords.lat, lng: pendingCoords.lng }));
                  setShowMapConfirm(false);
                  toast.success("Location confirmed");
                }}
                className="bg-[#34C759] text-white text-sm font-semibold px-5 py-2.5 rounded hover:bg-[#2DB84E] transition-colors"
                data-testid="map-confirm-btn"
              >
                ✓ Confirm Location
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );

  if (embedded) {
    return (
      <div data-testid="shop-application-embedded">
        {formBody}
        <SupportChat showLabel />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="shop-application-page">
        <div className="label-eyebrow mb-3">VERIFY YOUR BUSINESS</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">Verify your business.</h1>
        <p className="text-zinc-400 mb-10 max-w-2xl">Quick verification — takes about 10 minutes. All documents are private.</p>
        {formBody}
      </main>
      <SupportChat showLabel />
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="border border-white/10 bg-[#121214]">
      <div className="px-6 py-5 border-b border-white/10 flex items-start gap-3">
        <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight">{title}</h2>
          {hint && <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="px-6 py-6">{children}</div>
    </section>
  );
}

function Grid({ cols = 2, className = "", children }) {
  return <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4 ${className}`}>{children}</div>;
}

function Field({ label, hint, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600 mt-1.5">{hint}</p>}
    </div>
  );
}

function Agreement({ k, checked, onChange, label, important }) {
  return (
    <label className={`flex items-start gap-3 text-sm cursor-pointer p-3 border ${checked ? "border-[#34C759]/40 bg-[#34C759]/5" : "border-white/10"} ${important ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : ""}`} data-testid={`app-agree-${k}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(k, e.target.checked)} className="mt-1 w-4 h-4 accent-[#FF3B30] shrink-0" />
      <span className={`${important ? "text-white" : "text-zinc-300"}`}>{label}</span>
    </label>
  );
}

function FileSingleUploader({ label, path, onChange, accept, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.path);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <div className="border border-dashed border-white/20 bg-[#09090B] p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {path ? (
            <>
              <FileText className="w-5 h-5 text-[#34C759] shrink-0" />
              <span className="text-xs font-mono truncate text-zinc-400">{path.split("/").pop()}</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-zinc-600 shrink-0" />
              <span className="text-sm text-zinc-500">No file uploaded yet.</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => ref.current?.click()} className="btn-secondary text-xs py-2 px-4" data-testid={`${testid}-pick`}>
            {uploading ? "…" : path ? "Replace" : "Upload"}
          </button>
          {path && (
            <button type="button" onClick={() => onChange("")} className="text-zinc-500 hover:text-[#FF3B30]" data-testid={`${testid}-clear`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid={`${testid}-input`} />
    </div>
  );
}

function FileMultiUploader({ label, paths, onChange, accept, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...paths];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(res.data.path);
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    onChange(next);
    setUploading(false);
  };

  const remove = (p) => onChange(paths.filter((x) => x !== p));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="label-eyebrow">{label}</label>
        <button type="button" onClick={() => ref.current?.click()} className="btn-secondary text-xs py-2 flex items-center gap-1.5" data-testid={`${testid}-pick`}>
          <Upload className="w-3.5 h-3.5" /> {uploading ? "…" : "Add photos"}
        </button>
        <input ref={ref} type="file" accept={accept} multiple className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      </div>
      {paths.length === 0 ? (
        <div className="border border-dashed border-white/20 p-6 text-center text-sm text-zinc-500">
          Upload at least 3 photos showing your shop / mobile rig.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {paths.map((p) => (
            <div key={p} className="relative aspect-square border border-white/10 bg-[#09090B] overflow-hidden" data-testid={`${testid}-item-${p}`}>
              <img src={authFileUrl(p)} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <button onClick={() => remove(p)} className="absolute top-1 right-1 bg-[#09090B]/80 text-zinc-400 hover:text-[#FF3B30] p-1 z-10">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
